/** Importing, fading and framing the reference model. */

import type { ReferenceInfo } from "../../engine/reference.ts";
import type { Result } from "../../lib/result.ts";
import { getEngine } from "../engine/engine.actions.ts";
import { setHud } from "../hud/hud.actions.ts";
import { referenceState } from "./reference.state.ts";

export const UNIT_OPTIONS: readonly { value: number; label: string }[] = [
  { value: 1, label: "metres" },
  { value: 0.01, label: "centimetres" },
  { value: 0.001, label: "millimetres" },
  { value: 0.3048, label: "feet" },
];

function describe(info: ReferenceInfo): string {
  const { size, origin } = info;
  const dimensions = `${size.x.toFixed(1)} × ${size.y.toFixed(1)} × ${size.z.toFixed(1)} m`;
  const at = `${origin.x.toFixed(2)}, ${origin.y.toFixed(2)}, ${origin.z.toFixed(2)}`;

  const placement = info.centred
    ? "moved to origin — no longer aligned with the source file"
    : `at ${at} as exported`;

  return `${info.name} — ${info.meshCount} mesh · ${dimensions} · ${placement}`;
}

export async function importReference(file: File): Promise<Result<null>> {
  const { unit, centre, opacity } = referenceState.get();
  referenceState.set({ loading: true, error: null });

  const engine = getEngine();
  const result = await engine.reference.load(file, { scale: unit, centre });

  if (result.error) {
    referenceState.set({ loading: false, error: result.error.msg });
    setHud(result.error.msg);
    return { error: result.error };
  }

  const info = result.data;
  if (!info) {
    referenceState.set({ loading: false });
    return { data: null };
  }

  engine.reference.setOpacity(opacity / 100);
  // The model keeps its exported coordinates, so the ground goes to the model.
  engine.terrain.setOrigin(info.origin.x, info.origin.z);
  engine.afterReferenceChange();
  engine.frameReference();
  referenceState.set({ loading: false, error: null, info: describe(info) });
  setHud(
    info.centred
      ? "Reference model loaded and moved to the origin"
      : "Reference model loaded at its exported position — build around it",
  );

  return { data: null };
}

export function removeReference(): void {
  const engine = getEngine();

  engine.reference.clear();
  engine.terrain.setOrigin(0, 0);
  engine.afterReferenceChange();
  referenceState.set({ info: null, error: null });
}

export function goToReference(): void {
  if (!getEngine().frameReference()) {
    setHud("No reference model");
  }
}

export function setReferenceOpacity(opacity: number): void {
  referenceState.set({ opacity });
  getEngine().reference.setOpacity(opacity / 100);
}

export function setReferenceUnit(unit: number): void {
  referenceState.set({ unit });
  if (referenceState.get().info) {
    setHud("Re-import the file to apply the new unit");
  }
}

export function setReferenceCentre(centre: boolean): void {
  referenceState.set({ centre });
}

export function setReferenceCollide(collide: boolean): void {
  referenceState.set({ collide });
  getEngine().setReferenceCollides(collide);
}
